import {Program, Texture, FrameBuffer, vec3} from '../../beam/index';
import settings from '../settings'
import setUniform from '../setUniform'
import DenoiserRenderPass from './DenoiserRenderPass'
import cloudsVS from '../shaders/clouds-vs.glsl';
import cloudsFS from '../shaders/clouds-fs.glsl';
import copyVS from '../shaders/copy-vs.glsl';
import copyFS from '../shaders/copy-fs.glsl';

const CLOUDS_UPSCALE = 1;
let time = 0;

let voxelCloudData = [
    // { voxelsPerSide: [256,256], resolution: [4096, 4096] }, //16 X 16
    { voxelsPerSide: [128,128], resolution: [2048, 1024] }, //16 X 8
    { voxelsPerSide: [64,64],   resolution: [512, 512]   }, //8 X 8
    { voxelsPerSide: [32,32],   resolution: [256, 128]   }, //8 X 4
    { voxelsPerSide: [16,16],   resolution: [64, 64]     }, //4 X 4
    { voxelsPerSide: [8,8],     resolution: [32, 16]     }, //4 X 2
    { voxelsPerSide: [4,4],     resolution: [8, 8]       }, //2 X 2
    { voxelsPerSide: [2,2],     resolution: [4, 4]       }  //2 X 2
]

export default class CloudsRenderPass {

    constructor(renderer) {
        
        this.renderer = renderer
        
        this.uniforms = {}

        this.material = new Program(this.renderer.gl, {
			vertexShader:   cloudsVS,
            fragmentShader: cloudsFS,
        });
        this.material.name = 'clouds material'        
        


        this.noiseTexture = Texture.fromUrl(this.renderer.gl, `assets/textures/noise/256x256.png`, {
            linear: true,
            wrapS: this.renderer.gl.REPEAT,
            wrapT: this.renderer.gl.REPEAT,
        });

        this.copyMaterial = new Program(this.renderer.gl,{
            vertexShader:   copyVS,
            fragmentShader: copyFS,
            depthTest:      false,
            blend:          false,
        });
        this.copyMaterial.name = 'Clouds Copy Material'


        this.cloudsInfoFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:   1,
            height:  1,
            linear: false,
            type: this.renderer.gl.FLOAT 
        });


        this.resolveFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:   1,
            height:  1,
            linear: true,
            type: this.renderer.gl.FLOAT 
        });
        this.denoisedResolveFrameBuffer= new FrameBuffer(this.renderer.gl, {
            width:   1,
            height:  1,
            linear: true,
            type: this.renderer.gl.FLOAT 
        });
        this.denoiserPass = new DenoiserRenderPass(this.renderer, { 
            useAtrouNoiseFilter: false, useNeighbourgClipping: false, useVariance: false, useLongHistory:false, useTemporalUpscale: false, bilateralBlurIterations: 0, 
            spatialThreshold: 1,
            temporalThreshold: 0.9 
        })


        this.resolveAlphaFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:   1,
            height:  1,
            linear: true,
            type: this.renderer.gl.FLOAT 
        });
        this.denoisedResolveAlphaFrameBuffer= new FrameBuffer(this.renderer.gl, {
            width:   1,
            height:  1,
            linear: true,
            type: this.renderer.gl.FLOAT 
        });
        this.denoiserAlphaPass = new DenoiserRenderPass(this.renderer, { 
            useAtrouNoiseFilter: false, useNeighbourgClipping: false, useVariance: false, useLongHistory:false, useTemporalUpscale: false, bilateralBlurIterations: 0, 
            spatialThreshold: 1,
            temporalThreshold: 0.9 
        })


        this.lastDepthFrameBuffer  = new FrameBuffer(this.renderer.gl, {
            width:   1, 
            height:  1, 
            linear: false,
            type: this.renderer.gl.FLOAT 
        });
    }


    resize(w, h) 
    {
        this.mult = settings.cloudMult
        this.w = w;
        this.h = h;
        this.cloudsInfoFrameBuffer.resize( this.w * this.mult, this.h * this.mult )
        
        this.resolveFrameBuffer.resize( this.w * this.mult, this.h * this.mult )
        this.denoisedResolveFrameBuffer.resize( this.w * this.mult, this.h * this.mult )
        this.denoiserPass.resize( this.w * this.mult, this.h  * this.mult)

        this.resolveAlphaFrameBuffer.resize( this.w* this.mult , this.h* this.mult  )
        this.denoisedResolveAlphaFrameBuffer.resize( this.w* this.mult , this.h* this.mult  )
        this.denoiserAlphaPass.resize( this.w * this.mult, this.h * this.mult )

        this.lastDepthFrameBuffer.resize( this.w , this.h  )
    }

    render(bufferScene, bufferPlane, bufferCamera, input, output, depth) 
    {
        this.material.setDefine('CLOUD_STEPS', (settings.uCloudsMaxSteps>>0));
        this.material.setDefine('LIGHT_STEPS', (settings.uCloudsLightMaxSteps>>0));

        time += 0.03;
        let sun = vec3.create()
        vec3.set(sun, 0.0, 0.0, 1.0)
        vec3.rotateX( sun, sun, [0,0,0], -settings.uSunPositionY*Math.PI/2.0 );
        vec3.rotateY( sun, sun, [0,0,0], -settings.uSunPositionX*Math.PI + Math.PI/2    );

        this.uniforms.iResolution                     = [this.w*this.mult,this.h*this.mult]
        this.uniforms.tNoise                          = this.noiseTexture;
        this.uniforms.iTime                           = time;
        this.uniforms.uSunScatterMax                  = settings.uSunScatterMax 
        this.uniforms.uSunScatterStep                 = settings.uSunScatterStep 
        this.uniforms.uCloudExtinction                = settings.uCloudExtinction
        this.uniforms.uCloudSunIntensity              = settings.uCloudSunIntensity
        this.uniforms.uCloudsLightAbsorptionTowardSun = settings.uCloudsLightAbsorptionTowardSun
        this.uniforms.uLightAbsorptionThroughCloud    = settings.uLightAbsorptionThroughCloud
        this.uniforms.uCloudsDarknessThreshold        = settings.uCloudsDarknessThreshold
        this.uniforms.uLightStepSize                  = settings.uLightStepSize 
        this.uniforms.uCloudsStepSize                 = settings.uCloudsStepSize 
        this.uniforms.uCloudsMult                     = settings.uCloudsMult 
        this.uniforms.uCloudsOffset                   = settings.uCloudsOffset 
        this.uniforms.uCloudsDownscale                = settings.uCloudsDownscale 
        this.uniforms.uCloudsCenter                   = settings.uCloudsCenter 
        this.uniforms.uCloudsMaxSteps                 = (settings.uCloudsMaxSteps>>0) 
        this.uniforms.uCloudsLightMaxSteps            = (settings.uCloudsLightMaxSteps>>0)
        this.uniforms.uPowderCoef                     = settings.uPowderCoef
        this.uniforms.uCloudCoverage                  = settings.uCloudCoverage
        this.uniforms.uCloudCoverageSmoothing         = settings.uCloudCoverageSmoothing
        this.uniforms.uWorleyScale0                   = settings.uWorleyScale0
        this.uniforms.uWorleyLvl0                     = settings.uWorleyLvl0
        this.uniforms.uWorleyLvl1                     = settings.uWorleyLvl1
        this.uniforms.uWorleyLvl2                     = settings.uWorleyLvl2
        this.uniforms.uWorleyLvl3                     = settings.uWorleyLvl3
        this.uniforms.uPhaseParams                    = [settings.uCloudsforwardScattering, settings.uCloudsbackScattering, settings.uCloudsbaseBrightness, settings.uCloudsCloudsphaseFactor]
        this.uniforms.uSunDirection                   = sun;
        this.uniforms.uSunPosition                    = [ settings.uSunPositionX, settings.uSunPositionY ];
        this.uniforms.useNeighbourgClipping           =settings.useNeighbourgClipping
        this.uniforms.tCloudInfos                     =this.lastDepthFrameBuffer
        this.uniforms.tClouds                         = this.lastDepthFrameBuffer
        this.uniforms.tCloudsAlpha                    = this.lastDepthFrameBuffer
        this.uniforms.tInput                          =input
        this.uniforms.tDepth                          =depth
        this.uniforms.iResolution                     = [this.w*this.mult,this.h*this.mult]

        for (let k in this.uniforms) {
            if (this.material.uniforms[k]) {
                // this.material.uniforms[k].value = this.uniforms[k]
                this.material.setUniform( k, this.uniforms[k] )
            }
        }

        bufferPlane.material =	this.material


        this.renderer.clearColor(1,0,0,0)
        this.renderer.clear()
        this.material.setUniform('uPass', 0 )
        bufferPlane.id   = 'Clouds Render Pass'
        bufferPlane.name = 'Clouds Render Pass'
        this.renderer.render( bufferScene, bufferCamera, this.cloudsInfoFrameBuffer );


        this.renderer.clearColor(0,0,0,0)
        this.renderer.clear()
        this.material.setUniform('uPass', 1 )//cloud merged with input based on transmittance
        this.material.setUniform('tCloudInfos', this.cloudsInfoFrameBuffer )//update clouds infos
        bufferPlane.id   = 'Clouds Resolve'
        bufferPlane.name = 'Clouds Resolve'
        this.renderer.render( bufferScene, bufferCamera, this.resolveFrameBuffer );
        this.denoiserPass.render( bufferScene, bufferPlane, bufferCamera, this.resolveFrameBuffer, this.denoisedResolveFrameBuffer, this.cloudsInfoFrameBuffer)//, this.lastDepthFrameBuffer )


        bufferPlane.material = this.material
        this.renderer.clearColor(0,0,0,0)
        this.renderer.clear()
        this.material.setUniform('uPass', 2 )//cloud alpha transmittance denoising
        this.material.setUniform('tCloudInfos', this.cloudsInfoFrameBuffer )//update clouds infos
        bufferPlane.id   = 'Clouds Resolve Alpha'
        bufferPlane.name = 'Clouds Resolve Alpha'
        this.renderer.render( bufferScene, bufferCamera, this.resolveAlphaFrameBuffer );
        this.denoiserAlphaPass.render( bufferScene, bufferPlane, bufferCamera, this.resolveAlphaFrameBuffer, this.denoisedResolveAlphaFrameBuffer, this.cloudsInfoFrameBuffer)//, this.lastDepthFrameBuffer )


        this.renderer.clearColor(0,0,0,0)
        this.renderer.clear()
        bufferPlane.material = this.material
        this.material.setUniform('uPass', 3)//output color
        this.material.setUniform('tInput', input)
        this.material.setUniform('tClouds', this.denoisedResolveFrameBuffer)
        this.material.setUniform('tCloudsAlpha', this.denoisedResolveAlphaFrameBuffer)
        bufferPlane.id   = 'Clouds Copy Ouput'
        bufferPlane.name = 'Clouds Copy Ouput'
        this.renderer.render( bufferScene, bufferCamera, output );//original depth merged with cloud depth


        //COPY depth
        // bufferPlane.material = this.copyMaterial;
        // this.renderer.clearColor(-1,0,0,0)
        // this.copyMaterial.setUniform('tInput', this.cloudsInfoFrameBuffer)
        // this.renderer.clear()
        // bufferPlane.id   = 'clouds__copyLastDepth'
        // bufferPlane.name = 'clouds__copyLastDepth'
        // this.renderer.render(bufferScene, bufferCamera, this.lastDepthFrameBuffer);

    }
    
}